// ============================================================
// Datei: src/pages/ressorts/KasseMobile.jsx
// PURPOSE:
// Mobile Layout für Ressort Kasse
//
// ÄNDERUNGEN:
// - Helferbereich jetzt wie bei Kaffimobil mit 2 Subtabs:
//   1) Ressort
//   2) Helferpool
// - Bestehende VolunteerList bleibt im Tab "Ressort"
// - VolunteerPool bleibt im Tab "Helferpool"
// - Nur notwendige Anpassungen vorgenommen
// ============================================================

import React, { useMemo, useState } from "react";

import VolunteerList from "../../../components/VolunteerList";
import VolunteerPool from "../../../components/VolunteerPool";
import ShiftTable from "../../../components/shiftTable/ShiftTable";
import GuestRegistration from "../../../components/guestRegistration/GuestRegistration";
import GuestList from "../../../components/guestList/GuestList";
import "./kasseMobile.css";

// ============================================================
// GÄSTE-FILTER FÜR NORMALE OK-ANSICHT
// ============================================================
const normalizeText = (value) => String(value || "").trim();

const getLoggedInVolunteerId = () => {
    return localStorage.getItem("volunteerId") || localStorage.getItem("volunteer_id") || "";
};

const getLoggedInUsername = () => {
    return normalizeText(localStorage.getItem("username") || "").toLowerCase();
};

const isGuestRecordedByCurrentOk = (guest) => {
    const loggedInVolunteerId = getLoggedInVolunteerId();
    const loggedInUsername = getLoggedInUsername();

    if (loggedInVolunteerId && guest?.recordedById != null) {
        return String(guest.recordedById) === String(loggedInVolunteerId);
    }

    if (loggedInUsername && guest?.recordedByFullName) {
        return String(guest.recordedByFullName).toLowerCase().includes(loggedInUsername);
    }

    return false;
};

const filterGuestsForCurrentOk = (guests = []) => {
    if (!Array.isArray(guests)) {
        return [];
    }

    return guests.filter(isGuestRecordedByCurrentOk);
};


const KasseMobile = ({
    volunteers,
    setVolunteers,
    shifts,
    guests,
    activeTab,
    setActiveTab,
    reloadGuests,
    downloadCcEmails,
    reloadVolunteers,
    setSelectedVolunteer
}) => {
    // ============================================================
    // STATE
    // Untertab für "Libellen"
    // ============================================================
    const [helperSubTab, setHelperSubTab] = useState("ressort");

    // ============================================================
    // SHIFT FILTER
    // ============================================================
    const kasseEingang = shifts.filter(
        (s) => s.shiftId >= 201 && s.shiftId <= 207
    );

    const kasseHelfer = shifts.filter(
        (s) => s.shiftId >= 211 && s.shiftId <= 216
    );

    // ============================================================
    // Normale Gästeliste: nur eigene Gäste des eingeloggten OK
    // ============================================================
    const ownGuests = useMemo(() => {
        return filterGuestsForCurrentOk(guests);
    }, [guests]);

    // ============================================================
    // HAUPT-TABS
    // ============================================================
    const tabs = [
        { id: "overview", label: "Übersicht" },
        { id: "helpers", label: "Libellen" },
        { id: "shifts", label: "Schichtplan" },
        { id: "guests", label: "Gästeliste" },
        { id: "extras", label: "Extrawürscht" }
    ];

    return (
        <div className="tabs-container">
            <div className="tab-bar">
                {tabs.map((t) => (
                    <button
                        key={t.id}
                        className={
                            "tab-button" +
                            (activeTab === t.id ? " tab-button-active" : "")
                        }
                        onClick={() => setActiveTab(t.id)}
                        type="button"
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            <div className="tab-panels">
                {/* ========================================================
                   LIBELLEN
                   ======================================================== */}
                {activeTab === "helpers" && (
                    <div className="kasse-mobile-helpers-panel">
                        {/* ------------------------------------------------
                           SUBTABS wie bei Kaffimobil
                           ------------------------------------------------ */}
                        <div className="helpers-subtab-bar">
                            <button
                                type="button"
                                className={
                                    "helpers-subtab-button" +
                                    (helperSubTab === "ressort"
                                        ? " helpers-subtab-button-active"
                                        : "")
                                }
                                onClick={() => setHelperSubTab("ressort")}
                            >
                                Ressort
                            </button>

                            <button
                                type="button"
                                className={
                                    "helpers-subtab-button" +
                                    (helperSubTab === "pool"
                                        ? " helpers-subtab-button-active"
                                        : "")
                                }
                                onClick={() => setHelperSubTab("pool")}
                            >
                                Helferpool
                            </button>
                        </div>

                        {/* --------------------------------------------
                           TAB 1 = RESSORT
                           Bestehende Liste bleibt unverändert
                           -------------------------------------------- */}
                        {helperSubTab === "ressort" && (
                            <VolunteerList
                                volunteers={volunteers}
                                setVolunteers={setVolunteers}
                                onEdit={setSelectedVolunteer}
                                reloadVolunteers={reloadVolunteers}
                            />
                        )}

                        {/* --------------------------------------------
                           TAB 2 = HELFERPOOL
                           Bereits bestehender Pool
                           -------------------------------------------- */}
                        {helperSubTab === "pool" && (
                            <VolunteerPool
                                departmentName="Kasse"
                                onAssigned={() => {
                                    if (reloadVolunteers) {
                                        reloadVolunteers();
                                    }
                                }}
                            />
                        )}
                    </div>
                )}

                {/* ========================================================
                   SCHICHTPLAN
                   ======================================================== */}
                {activeTab === "shifts" && (
                    <>
                        <h3 className="kasse-subheading">Eingang</h3>
                        <ShiftTable shifts={kasseEingang} />

                        <h3 className="kasse-subheading">VIP</h3>
                        <ShiftTable shifts={kasseHelfer} />
                    </>
                )}

                {/* ========================================================
                   GÄSTELISTE
                   ======================================================== */}
                {activeTab === "guests" && (
                    <>
                        <GuestRegistration refreshGuestList={reloadGuests} />
                        <GuestList guests={ownGuests} />
                    </>
                )}

                {/* ========================================================
                   EXTRAWÜRSCHT
                   ======================================================== */}
                {activeTab === "extras" && (
                    <div
                        className="cc-mail-card"
                        onClick={downloadCcEmails}
                        role="button"
                        tabIndex={0}
                    >
                        <span className="cc-mail-icon">✉️</span>

                        <div className="cc-mail-text">
                            <span className="cc-mail-title">
                                CC-Liste Helferfest herunterladen
                            </span>
                            <span className="cc-mail-subtitle">
                                Für Outlook &amp; Gmail geeignet
                            </span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default KasseMobile;